import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { confirm } from "@/lib/confirm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { GitMerge, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic manual merge dialog for surgeons or anesthesiologists.
 * - endpoint: "/surgeons" or "/anesthesiologists" (also used to build merge URL)
 * - entityLabel: "cirurgião" or "anestesista"
 *
 * Flow:
 *   1) User picks a CANONICAL record (search + click)
 *   2) Then picks 1+ SOURCE records to be merged into the canonical
 *   3) Confirms — backend cascades cases + filtered_maps + adds aliases + deletes sources
 */
export default function MergeEntitiesDialog({
  open,
  onClose,
  endpoint,
  entityLabel,
  onMerged,
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [canonicalId, setCanonicalId] = useState(null);
  const [sourceIds, setSourceIds] = useState(new Set());
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open) {
      setCanonicalId(null);
      setSourceIds(new Set());
      setSearch("");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(endpoint);
        setList(r.data || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
      } finally { setLoading(false); }
    })();
  }, [open, endpoint]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return list;
    return list.filter((x) => {
      const hay = `${x.full_name || ""} ${x.crm || ""} ${(x.name_aliases || []).join(" ")}`.toLowerCase();
      return hay.includes(s);
    });
  }, [list, search]);

  const canonical = list.find((x) => x.id === canonicalId);

  function toggleSource(id) {
    if (id === canonicalId) return;
    setSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function doMerge() {
    if (!canonicalId || sourceIds.size === 0) {
      toast.error(`Selecione 1 ${entityLabel} canônico e ao menos 1 origem para mesclar.`);
      return;
    }
    const sources = Array.from(sourceIds);
    const sourceNames = sources.map((id) => list.find((x) => x.id === id)?.full_name).filter(Boolean).join(", ");
    if (!(await confirm({ description: `Mesclar para "${canonical?.full_name}":\n\n${sourceNames}\n\n` +
      `Os nomes virarão apelidos do cadastro canônico, ` +
      `casos vinculados serão reapontados, e os duplicados serão excluídos. Continuar?` }))) return;
    setMerging(true);
    try {
      const r = await api.post(`${endpoint}/${canonicalId}/merge`, { source_ids: sources });
      const stats = r.data?.merge_stats || {};
      toast.success(
        `${stats.merged || 0} cadastro(s) mesclado(s). ` +
        `${(stats.cases_principal_updated || 0) + (stats.cases_assistant_updated || 0)} caso(s) atualizado(s). ` +
        `${stats.aliases_added || 0} apelido(s) adicionado(s).`,
        { duration: 7000 },
      );
      onMerged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao mesclar");
    } finally { setMerging(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-purple-700" />
            Mesclar {entityLabel}s manualmente
          </DialogTitle>
          <DialogDescription>
            Escolha qual cadastro vai ficar (canônico) e quais vão ser absorvidos como apelidos.
            Use isso quando os cadastros têm CRMs diferentes ou um deles não tem CRM e o auto-merge não pegou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, CRM ou apelido..." className="pl-9"
              data-testid="merge-search" />
          </div>

          {canonical && (
            <div className="border-2 border-purple-300 bg-purple-50 rounded-md p-3 text-sm" data-testid="canonical-banner">
              <div className="text-xs uppercase tracking-wider text-purple-700 font-semibold mb-1">Canônico (vai ficar)</div>
              <div className="font-medium text-slate-900">{canonical.full_name}</div>
              <div className="text-xs text-slate-600">{canonical.crm || "sem CRM"}{canonical.specialty ? ` · ${canonical.specialty}` : ""}</div>
              <Button variant="link" size="sm" className="px-0 h-auto text-xs text-purple-700"
                onClick={() => { setCanonicalId(null); setSourceIds(new Set()); }}
                data-testid="btn-clear-canonical">
                Trocar canônico
              </Button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 w-20">Ação</th>
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-left px-3 py-2">CRM</th>
                  <th className="text-left px-3 py-2">Apelidos</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="text-center p-6 text-slate-400">Carregando…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={4} className="text-center p-6 text-slate-400">Nenhum resultado.</td></tr>
                )}
                {filtered.map((x) => {
                  const isCanonical = x.id === canonicalId;
                  const isSource = sourceIds.has(x.id);
                  return (
                    <tr key={x.id} className={`border-t border-slate-100 ${isCanonical ? "bg-purple-50" : isSource ? "bg-amber-50" : ""}`} data-testid={`merge-row-${x.id}`}>
                      <td className="px-3 py-2">
                        {isCanonical ? (
                          <span className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">Canônico</span>
                        ) : !canonicalId ? (
                          <Button size="sm" variant="outline" onClick={() => setCanonicalId(x.id)}
                            className="text-xs h-7" data-testid={`btn-pick-canonical-${x.id}`}>
                            Manter este
                          </Button>
                        ) : (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={isSource} onChange={() => toggleSource(x.id)}
                              data-testid={`chk-source-${x.id}`} />
                            <span className="text-xs text-slate-600">Mesclar</span>
                          </label>
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-900">{x.full_name}</td>
                      <td className="px-3 py-2 text-slate-600">{x.crm || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {(x.name_aliases || []).join(", ") || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {canonicalId && sourceIds.size > 0 && (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3" data-testid="merge-preview">
              <strong>{sourceIds.size}</strong> cadastro(s) serão absorvidos por <strong>{canonical?.full_name}</strong>.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={merging}>Cancelar</Button>
          <Button className="bg-purple-700 hover:bg-purple-800" onClick={doMerge}
            disabled={merging || !canonicalId || sourceIds.size === 0}
            data-testid="btn-confirm-merge">
            {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}
            Mesclar {sourceIds.size || ""} cadastro(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
